import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { formatCarImage } from '../../utils/carInventoryStorage';

// Define interfaces for car data
export interface GlobalCarVariant {
  name: string;
  colors?: string[];
  downPayment?: string;
  monthlyEmi?: string;
  rawDownPayment?: number;
  exShowroom?: string;
  onRoadPrice?: string;
}

export interface GlobalCarData {
  id: string;
  image: string;
  name: string;
  downPayment: string;
  monthlyEmi?: string;
  carType?: 't-permit' | 'private';
  variants: GlobalCarVariant[];
  // Financial details
  globalExShowroomPrice?: number;
  globalLoanAmount?: number;
  globalInterestRate?: number;
  globalProcessingFee?: number;
  globalInsurance?: number;
  globalRegistration?: number;
  globalOtherCharges?: number;
  globalTotalOnRoadPrice?: number;
}

interface GlobalCarsState {
  globalCarsList: GlobalCarData[];
  globalSelectedCar: GlobalCarData | null;
  globalSelectedVariant: string | null;
  globalSelectedBank: string | null;
  globalIsLoading: boolean;
  globalError: string | null;
  globalLastUpdated: string | null;
}

const initialState: GlobalCarsState = {
  globalCarsList: [],
  globalSelectedCar: null,
  globalSelectedVariant: null,
  globalSelectedBank: null,
  globalIsLoading: false,
  globalError: null,
  globalLastUpdated: null,
};

// Get fallback car data
const getFallbackCarData = (): GlobalCarData[] => [
  {
    id: 'maruti-suzuki-wagon-r',
    image: '/Website-Images/Cars/wagnor.jpg',
    name: 'Maruti Suzuki Wagon-R',
    carType: 't-permit',
    downPayment: '₹1,39,580',
    monthlyEmi: '₹13,108/month',
    globalExShowroomPrice: 665500,
    globalLoanAmount: 597370,
    globalInterestRate: 11.4,
    globalTotalOnRoadPrice: 745000,
    variants: [
      { name: 'H3 CNG', colors: ['White'], downPayment: '₹1,39,580', rawDownPayment: 139580, monthlyEmi: '₹13,108/month', exShowroom: '₹6,65,500' },
      { name: 'LXI CNG', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'], downPayment: '₹1,48,022', rawDownPayment: 148022, monthlyEmi: '₹13,162/month', exShowroom: '₹6,68,500' },
      { name: 'VXI CNG', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'], downPayment: '₹1,55,270', rawDownPayment: 155270, monthlyEmi: '₹13,971/month', exShowroom: '₹7,13,500' }
    ]
  },
  {
    id: 'maruti-suzuki-ertiga',
    image: '/Website-Images/Cars/ertiga.jpg',
    name: 'Maruti Suzuki ERTIGA',
    carType: 't-permit',
    downPayment: '₹2,60,864',
    monthlyEmi: '₹21,526/month',
    globalExShowroomPrice: 1112500,
    globalLoanAmount: 1021381,
    globalInterestRate: 9.65,
    globalTotalOnRoadPrice: 1224625,
    variants: [
      { name: 'Tour M CNG 1.5 MT', colors: ['White'], downPayment: '₹2,60,864', rawDownPayment: 260864, monthlyEmi: '₹21,526/month', exShowroom: '₹11,12,500' },
      { name: 'VXI CNG 1.5 MT', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'], downPayment: '₹2,63,218', rawDownPayment: 263218, monthlyEmi: '₹21,580/month', exShowroom: '₹11,15,500' },
      { name: 'ZXI CNG 1.5 MT', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'], downPayment: '₹2,83,013', rawDownPayment: 283013, monthlyEmi: '₹23,570/month', exShowroom: '₹12,25,500' }
    ]
  },
  {
    id: 'toyota-rumion',
    image: '/Website-Images/Cars/Ruminum.jpg',
    name: 'TOYOTA RUMION',
    carType: 't-permit',
    downPayment: '₹2,24,517',
    monthlyEmi: '₹22,574/month',
    globalExShowroomPrice: 1161500,
    globalLoanAmount: 1071098,
    globalInterestRate: 9.65,
    globalTotalOnRoadPrice: 1283115,
    variants: [
      { name: 'S CNG 1.5 MT', colors: ['White', 'Silver', 'Grey'], downPayment: '₹2,24,517', rawDownPayment: 224517, monthlyEmi: '₹22,574/month', exShowroom: '₹11,61,500' }
    ]
  },
  {
    id: 'hyundai-aura',
    image: '/Website-Images/Cars/Aura.jpg',
    name: 'HYUNDAI AURA',
    carType: 't-permit',
    downPayment: '₹1,27,854',
    monthlyEmi: '₹14,745/month',
    globalExShowroomPrice: 754800,
    globalLoanAmount: 671976,
    globalInterestRate: 11.4,
    globalTotalOnRoadPrice: 852900,
    variants: [
      { name: 'E CNG', colors: ['White', 'Silver', 'Grey', 'Cherry Night'], downPayment: '₹1,27,854', rawDownPayment: 127854, monthlyEmi: '₹14,745/month', exShowroom: '₹7,54,800' },
      { name: 'S CNG', colors: ['White', 'Silver', 'Grey', 'Cherry Night'], downPayment: '₹1,56,420', rawDownPayment: 156420, monthlyEmi: '₹16,053/month', exShowroom: '₹8,37,000' },
      { name: 'SX CNG', colors: ['White', 'Silver', 'Grey', 'Cherry Night'], downPayment: '₹1,70,720', rawDownPayment: 170720, monthlyEmi: '₹17,400/month', exShowroom: '₹9,11,000' }
    ]
  },
  {
    id: 'maruti-suzuki-dzire',
    image: '/Website-Images/Cars/Dzire.jpg',
    name: 'Maruti Suzuki Dzire',
    carType: 't-permit',
    downPayment: '₹2,10,098',
    monthlyEmi: '₹15,826/month',
    globalExShowroomPrice: 777000,
    globalLoanAmount: 721242,
    globalInterestRate: 11.4,
    globalTotalOnRoadPrice: 881520,
    variants: [
      { name: 'Tour\'s CNG', colors: ['White'], downPayment: '₹2,10,098', rawDownPayment: 210098, monthlyEmi: '₹15,826/month', exShowroom: '₹7,77,000' }
    ]
  },
  {
    id: 'toyota-innova-crysta',
    image: '/Website-Images/Cars/Crysta.jpg',
    name: 'Toyota Innova Crysta',
    carType: 't-permit',
    downPayment: '₹3,67,809',
    monthlyEmi: '₹30,065/month',
    globalExShowroomPrice: 1999000,
    globalLoanAmount: 1830891,
    globalInterestRate: 9.65,
    globalTotalOnRoadPrice: 2177490,
    variants: [
      { name: 'GX', colors: ['White', 'Silver', 'Pearl White'], downPayment: '₹3,67,809', rawDownPayment: 367809, monthlyEmi: '₹30,065/month', exShowroom: '₹19,99,000' },
      { name: 'GX+', colors: ['White', 'Silver', 'Pearl White'], downPayment: '₹4,20,664', rawDownPayment: 420664, monthlyEmi: '₹32,068/month', exShowroom: '₹21,71,000' },
      { name: 'VX', colors: ['White', 'Silver', 'Pearl White'], downPayment: '₹5,10,063', rawDownPayment: 510063, monthlyEmi: '₹37,057/month', exShowroom: '₹25,40,000' },
      { name: 'ZX', colors: ['White', 'Silver', 'Pearl White'], downPayment: '₹5,69,148', rawDownPayment: 569148, monthlyEmi: '₹39,138/month', exShowroom: '₹27,08,000' }
    ]
  },
  {
    id: 'maruti-suzuki-swift',
    image: '/Website-Images/Cars/wagnor.jpg',
    name: 'Maruti Suzuki Swift',
    carType: 'private',
    downPayment: '₹1,25,000',
    monthlyEmi: '₹11,500/month',
    globalExShowroomPrice: 649000,
    globalLoanAmount: 580000,
    globalInterestRate: 8.85,
    globalTotalOnRoadPrice: 735000,
    variants: [
      { name: 'LXI MT', colors: ['Pearl Arctic White', 'Magma Grey', 'Splendid Silver', 'Luster Blue'], downPayment: '₹1,25,000', rawDownPayment: 125000, monthlyEmi: '₹11,500/month', exShowroom: '₹6,49,000' },
      { name: 'VXI MT', colors: ['Pearl Arctic White', 'Magma Grey', 'Splendid Silver', 'Luster Blue', 'Sizzling Red'], downPayment: '₹1,45,000', rawDownPayment: 145000, monthlyEmi: '₹12,800/month', exShowroom: '₹7,29,000' },
      { name: 'ZXI MT', colors: ['Pearl Arctic White', 'Magma Grey', 'Splendid Silver', 'Luster Blue', 'Sizzling Red'], downPayment: '₹1,65,000', rawDownPayment: 165000, monthlyEmi: '₹14,200/month', exShowroom: '₹8,29,000' }
    ]
  },
  {
    id: 'hyundai-creta',
    image: '/Website-Images/Cars/Crysta.jpg',
    name: 'Hyundai Creta',
    carType: 'private',
    downPayment: '₹2,20,000',
    monthlyEmi: '₹21,000/month',
    globalExShowroomPrice: 1100000,
    globalLoanAmount: 980000,
    globalInterestRate: 8.85,
    globalTotalOnRoadPrice: 1280000,
    variants: [
      { name: 'E 1.5 Petrol', colors: ['Atlas White', 'Titan Grey', 'Abyss Black', 'Ranger Khaki'], downPayment: '₹2,20,000', rawDownPayment: 220000, monthlyEmi: '₹21,000/month', exShowroom: '₹11,00,000' },
      { name: 'EX 1.5 Petrol', colors: ['Atlas White', 'Titan Grey', 'Abyss Black', 'Ranger Khaki', 'Fiery Red'], downPayment: '₹2,50,000', rawDownPayment: 250000, monthlyEmi: '₹23,500/month', exShowroom: '₹12,21,000' },
      { name: 'SX 1.5 Petrol', colors: ['Atlas White', 'Titan Grey', 'Abyss Black', 'Ranger Khaki', 'Fiery Red'], downPayment: '₹3,10,000', rawDownPayment: 310000, monthlyEmi: '₹28,500/month', exShowroom: '₹15,30,000' }
    ]
  },
  {
    id: 'toyota-urban-cruiser-hyryder',
    image: '/Website-Images/Cars/hyryder.avif',
    name: 'Toyota Urban Cruiser Hyryder',
    carType: 'private',
    downPayment: '₹2,69,810',
    monthlyEmi: '₹27,415/month',
    globalExShowroomPrice: 1381000,
    globalLoanAmount: 1300000,
    globalInterestRate: 9.65,
    globalTotalOnRoadPrice: 1558810,
    variants: [
      { name: 'S CNG 1.5 MT', colors: ['White', 'Silver', 'Grey', 'Red'], downPayment: '₹2,69,810', rawDownPayment: 269810, monthlyEmi: '₹27,415/month', exShowroom: '₹13,81,000' },
      { name: 'G CNG 1.5 MT', colors: ['White', 'Silver', 'Grey', 'Red', 'Blue'], downPayment: '₹3,00,090', rawDownPayment: 300090, monthlyEmi: '₹31,000/month', exShowroom: '₹15,59,000' }
    ]
  },
  {
    id: 'toyota-innova-hycross',
    image: '/Website-Images/Cars/hycross.webp',
    name: 'Toyota Innova Hycross',
    carType: 'private',
    downPayment: '₹3,97,770',
    monthlyEmi: '₹38,950/month',
    globalExShowroomPrice: 1977000,
    globalLoanAmount: 1850000,
    globalInterestRate: 9.65,
    globalTotalOnRoadPrice: 2227770,
    variants: [
      { name: 'GX 7STR Petrol', colors: ['Super White', 'Silver Metallic', 'Attitude Black'], downPayment: '₹3,97,770', rawDownPayment: 397770, monthlyEmi: '₹38,950/month', exShowroom: '₹19,77,000' },
      { name: 'VX 7STR Hybrid', colors: ['Super White', 'Silver Metallic', 'Attitude Black'], downPayment: '₹5,09,470', rawDownPayment: 509470, monthlyEmi: '₹50,950/month', exShowroom: '₹25,97,000' }
    ]
  }
];

// Helper to merge backend cars with fallback cars (ensuring private cars like Swift, Creta, Hyryder, Hycross are available)
const mergeBackendAndFallbackCars = (carSummaries: GlobalCarData[]): GlobalCarData[] => {
  const fallbackCars = getFallbackCarData();
  const existingNames = new Set(
    carSummaries.map((c) => c.name.toLowerCase().replace(/[^a-z0-9]/g, ''))
  );

  const missingCars = fallbackCars.filter((fc) => {
    const cleanName = fc.name.toLowerCase().replace(/[^a-z0-9]/g, '');
    return !existingNames.has(cleanName);
  });

  return [...carSummaries, ...missingCars].map((car) => ({
    ...car,
    image: formatCarImage(car.image)
  }));
};

// Async thunk for fetching car data
export const globalFetchCarsData = createAsyncThunk(
  'globalCars/fetchCarsData',
  async (_, { rejectWithValue }) => {
    try {
      // Try to fetch from backend with cache busting
      const quotationService = await import('../../services/quotationService');
      const carSummaries = await quotationService.default.getCarSummaries();
      
      if (carSummaries && carSummaries.length > 0) {
        console.log('✅ Successfully fetched car data from backend');
        console.log('📊 Car data summary:', carSummaries.map((car: GlobalCarData) => ({
          name: car.name,
          variantCount: car.variants?.length || 0,
          variants: car.variants?.map((v: GlobalCarVariant) => v.name) || []
        })));
        return mergeBackendAndFallbackCars(carSummaries);
      } else {
        console.log('⚠️ Backend returned empty data, using fallback');
        return getFallbackCarData();
      }
    } catch (error) {
      console.log('⚠️ Backend fetch failed, using fallback:', error);
      // Always return fallback data instead of rejecting
      return getFallbackCarData();
    }
  }
);

// Force refresh thunk that bypasses cache
export const globalForceRefreshCarsData = createAsyncThunk(
  'globalCars/forceRefreshCarsData',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Force refreshing car data...');
      // Clear any cached data and force fresh fetch
      const quotationService = await import('../../services/quotationService');
      const carSummaries = await quotationService.default.getCarSummaries();
      
      if (carSummaries && carSummaries.length > 0) {
        console.log('✅ Successfully force refreshed car data from backend');
        console.log('📊 Refreshed car data:', carSummaries.map((car: GlobalCarData) => ({
          name: car.name,
          variantCount: car.variants?.length || 0,
          variants: car.variants?.map((v: GlobalCarVariant) => v.name) || []
        })));
        return mergeBackendAndFallbackCars(carSummaries);
      } else {
        console.log('⚠️ Backend returned empty data after force refresh, using fallback');
        return getFallbackCarData();
      }
    } catch (error) {
      console.log('⚠️ Force refresh failed, using fallback:', error);
      return getFallbackCarData();
    }
  }
);

const globalCarsSlice = createSlice({
  name: 'globalCars',
  initialState,
  reducers: {
    globalSetSelectedCar: (state, action: PayloadAction<GlobalCarData>) => {
      state.globalSelectedCar = action.payload;
    },
    globalSetSelectedVariant: (state, action: PayloadAction<string>) => {
      state.globalSelectedVariant = action.payload;
    },
    globalSetSelectedBank: (state, action: PayloadAction<string>) => {
      state.globalSelectedBank = action.payload;
    },
    globalUpdateCarFinancialData: (state, action: PayloadAction<{ carId: string; financialData: Partial<GlobalCarData> }>) => {
      const { carId, financialData } = action.payload;
      const carIndex = state.globalCarsList.findIndex(car => car.id === carId);
      if (carIndex !== -1) {
        state.globalCarsList[carIndex] = { ...state.globalCarsList[carIndex], ...financialData };
      }
      // Update selected car if it matches
      if (state.globalSelectedCar?.id === carId) {
        state.globalSelectedCar = { ...state.globalSelectedCar, ...financialData };
      }
    },
    globalClearError: (state) => {
      state.globalError = null;
    },
    globalResetCarSelection: (state) => {
      state.globalSelectedCar = null;
      state.globalSelectedVariant = null;
      state.globalSelectedBank = null;
    },
    globalClearCache: (state) => {
      state.globalCarsList = [];
      state.globalLastUpdated = null;
      state.globalError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle regular fetch
      .addCase(globalFetchCarsData.pending, (state) => {
        state.globalIsLoading = true;
        state.globalError = null;
      })
      .addCase(globalFetchCarsData.fulfilled, (state, action) => {
        state.globalIsLoading = false;
        state.globalCarsList = action.payload;
        state.globalLastUpdated = new Date().toISOString();
        state.globalError = null;
      })
      .addCase(globalFetchCarsData.rejected, (state, action) => {
        state.globalIsLoading = false;
        state.globalError = action.payload as string;
      })
      // Handle force refresh
      .addCase(globalForceRefreshCarsData.pending, (state) => {
        state.globalIsLoading = true;
        state.globalError = null;
      })
      .addCase(globalForceRefreshCarsData.fulfilled, (state, action) => {
        state.globalIsLoading = false;
        state.globalCarsList = action.payload;
        state.globalLastUpdated = new Date().toISOString();
        state.globalError = null;
      })
      .addCase(globalForceRefreshCarsData.rejected, (state, action) => {
        state.globalIsLoading = false;
        state.globalError = action.payload as string;
      });
  },
});

export const {
  globalSetSelectedCar,
  globalSetSelectedVariant,
  globalSetSelectedBank,
  globalUpdateCarFinancialData,
  globalClearError,
  globalResetCarSelection,
  globalClearCache,
} = globalCarsSlice.actions;

export default globalCarsSlice.reducer;